import { migrateTraversal } from "../helpers/migrateTraversal";
import { IWebsiteBuilderMigrationNode, IWebsiteBuilderSchemaVersionMigrationNode } from "../helpers/migrationNode";
import { WebsiteBuilderMigrationError } from "../helpers/migrationError";

type TMigrateButtonNoneLinkV6Result = {
  type: "none";
}

type TMigrateButtonUrlLinkV6Result = {
  type: "url";
  href: string;
  blankTarget: boolean;
}

type TMigrateButtonLinkV6Result = TMigrateButtonNoneLinkV6Result | TMigrateButtonUrlLinkV6Result;

type TMigrateLinkUrlDestinationV6Result = {
  type: "url";
  href: string;
  blankTarget: boolean;
}

type TMigrateLinkDestinationV6Result = TMigrateLinkUrlDestinationV6Result;

interface IMigrateElementV5Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: string;
    settings: Record<string, unknown>;
  }
}

export interface IMigrateButtonV5Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "button";
    settings: {
      link: string;
      openLinkInNewTab: boolean;
    }
  }
}

export interface IMigrateButtonV6Result extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "button";
    settings: {
      link: TMigrateButtonLinkV6Result;
    }
  }
}

export interface IMigrateLinkV5Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "link";
    settings: {
      href: string;
      openLinkInNewTab: boolean;
    }
  }
}

export interface IMigrateLinkV6Result extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "link";
    settings: {
      destination: TMigrateLinkDestinationV6Result;
    }
  }
}

function isMigrateElementV5Input(node: any): node is IMigrateElementV5Input {
  const partialNode: Partial<IMigrateElementV5Input> = node;

  return (
    partialNode?.type === "element" &&
    partialNode.element?.type != null &&
    "settings" in partialNode.element
  );
}

function isMigrateButtonV5Input(node: IMigrateElementV5Input): node is IMigrateButtonV5Input {
  return (
    node.element.type === "button" &&
    node.element.settings != null &&
    "link" in node.element.settings &&
    "openLinkInNewTab" in node.element.settings &&
    (node.element.settings.link == null || typeof node.element.settings.link === "string") &&
    (node.element.settings.openLinkInNewTab == null || typeof node.element.settings.openLinkInNewTab === "boolean")
  );
}

function isMigrateLinkV5Input(node: IMigrateElementV5Input): node is IMigrateLinkV5Input {
  return (
    node.element.type === "link" &&
    node.element.settings != null &&
    "href" in node.element.settings &&
    "openLinkInNewTab" in node.element.settings &&
    (node.element.settings.href == null || typeof node.element.settings.href === "string") &&
    (node.element.settings.openLinkInNewTab == null || typeof node.element.settings.openLinkInNewTab === "boolean")
  );
}

export function migrateSchemaV5ToV6(node: IWebsiteBuilderSchemaVersionMigrationNode): IWebsiteBuilderSchemaVersionMigrationNode {
  if (node.schemaVersion !== 5) {
    throw new WebsiteBuilderMigrationError({
      type: "cannot-migrate-from-schema-version",
      schemaVersion: node.schemaVersion,
      nextSchemaVersion: 6,
    });
  }

  function migrateNode(prev: IWebsiteBuilderMigrationNode): IWebsiteBuilderMigrationNode {
    const throwElementMissingFieldError = () => {
      throw new WebsiteBuilderMigrationError({
        type: "element-missing-field",
        schemaVersion: 5,
        nextSchemaVersion: 6,
      });
    };
    
    if (prev?.type === "element") {
      if (!isMigrateElementV5Input(prev)) {
        throwElementMissingFieldError();
      } else {
        if (prev.element.type === "button" || prev.element.type === "link") {
          if (!isMigrateButtonV5Input(prev) && !isMigrateLinkV5Input(prev)) {
            throwElementMissingFieldError();
          } else {
            const prevOpenLinkInNewTab = prev.element.settings.openLinkInNewTab ?? false;

            delete prev.element.settings.openLinkInNewTab;

            if (prev.element.type === "button") {
              const prevLink = prev.element.settings.link ?? "";
              const prevModifed = prevLink.length > 0 || prevOpenLinkInNewTab;

              const buttonLinkV6Result: TMigrateButtonLinkV6Result = prevModifed ? {
                type: "url",
                href: prevLink,
                blankTarget: prevOpenLinkInNewTab,
              } : { type: "none" };

              const buttonV6Result: IMigrateButtonV6Result = {
                ...prev,
                element: {
                  ...prev.element,
                  settings: {
                    ...prev.element.settings,
                    link: buttonLinkV6Result,
                  },
                },
              };

              return buttonV6Result;
            } else {
              const prevHref = prev.element.settings.href ?? "";

              delete prev.element.settings.href;

              const linkDestinationV6Result: TMigrateLinkDestinationV6Result = {
                type: "url",
                href: prevHref,
                blankTarget: prevOpenLinkInNewTab,
              };

              const linkV6Result: IMigrateLinkV6Result = {
                ...prev,
                element: {
                  ...prev.element,
                  settings: {
                    ...prev.element.settings,
                    destination: linkDestinationV6Result,
                  },
                },
              };

              return linkV6Result;
            }
          }
        } else {
          return prev;
        }
      }
    } else {
      return prev;
    }
  }

  return {
    ...migrateTraversal(node, migrateNode),
    schemaVersion: 6,
  };
}